import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { ChevronLeft, ChevronRight, Star, X } from "lucide-react";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { ImageDisplay } from "@/components/ui/image-display";
import { PropertyImageThumbnail } from "@/components/ui/property-image-thumbnail";
import type { Tag as PropertyTag } from "../../convex/types";
import { getStatusTags, isStatusTagName } from "@/lib/property-tags";

export interface CommonTypeFields {
  bedrooms?: number;
  bathrooms?: number;
  build_size?: number;
  land_size?: number;
  year_built?: number;
  total_price?: number;
  dp_minimal?: number;
  pks_bank?: string;
  price_per_year?: number;
  minimum_duration?: number;
  safety_deposit_amount?: number;
  pet_allowed?: string;
  price_for_duration?: number;
  leasehold_duration?: number;
  extension_duration?: number;
  land_zoning?: string;
  view?: string;
  road_access_width?: number;
  lease_duration?: number;
}

interface PropertyImage {
  key: string;
  filename: string;
  contentType?: string;
  size?: number;
  blurhash?: string;
}

interface PropertyBaseDetails {
  property_name?: string;
  reff_id?: string;
  shm_condition?: string;
  slf_pbg_condition?: string;
  zoning?: string;
  featured?: boolean;
  draft?: boolean;
  hidden?: boolean;
  latitude?: number;
  longitude?: number;
  address?: string;
  description?: string;
  images?: PropertyImage[];
  created_at?: number;
  created_by?: string;
  tags?: string[];
}

interface PropertyAreaDetails {
  area_name: string;
  country: string;
  province: string;
}

interface PropertyFacilitiesDetails {
  living_room_type?: string;
  dining_rooms?: number | string;
  kitchens?: number | string;
  extra_rooms?: number | string;
  electricity_power?: number;
  water_source?: string;
  parking?: number;
  view?: string;
  furniture?: string;
  internet?: boolean;
  swimming_pool?: boolean;
  storage?: boolean;
  garden?: boolean;
  security?: boolean;
}

export interface PropertyDetailsRecord {
  baseProperty: PropertyBaseDetails;
  propertyTypes?: string[];
  propertyType?: string | null;
  facilities?: PropertyFacilitiesDetails;
  area?: PropertyAreaDetails;
}

interface PropertyDetailListItem {
  label: string;
  value: ReactNode;
}

function formatPriceInRupiah(value: number): string {
  return new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    maximumFractionDigits: 0,
  }).format(value);
}

function formatPercentage(value: number): string {
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value);
}

function formatDisplayText(value: string): string {
  return value
    .split(/[_-\s]+/)
    .filter((segment) => segment.length > 0)
    .map((segment) => {
      if (segment.toUpperCase() === segment) {
        return segment;
      }

      return segment.charAt(0).toUpperCase() + segment.slice(1).toLowerCase();
    })
    .join(" ");
}

function PropertyDetailList({
  items,
}: {
  items: PropertyDetailListItem[];
}): ReactNode {
  return (
    <dl className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
      {items.map((item) => (
        <div key={item.label} className="space-y-1">
          <dt className="text-xs font-medium uppercase tracking-[0.12em] text-muted-foreground">
            {item.label}
          </dt>
          <dd className="text-sm font-medium leading-6 text-foreground">
            {item.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

interface PropertyDetailsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  property: PropertyDetailsRecord | null;
  propertyTypes: string[];
  typeDataMap?: Record<string, CommonTypeFields>;
  singleTypeData?: CommonTypeFields;
  isDetailedQueryLoading: boolean;
  tags?: PropertyTag[];
  formatPropertyType: (propertyType: string | null | undefined) => string;
  formatDate: (timestamp?: number) => string;
  formatCreator: (creator?: string) => string;
}

export function PropertyDetailsDialog({
  open,
  onOpenChange,
  property,
  propertyTypes,
  typeDataMap,
  singleTypeData,
  isDetailedQueryLoading,
  tags,
  formatPropertyType,
  formatDate,
  formatCreator,
}: PropertyDetailsDialogProps) {
  const propertyBase = property ? property.baseProperty : null;
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(
    null,
  );

  const handleGalleryKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (
        selectedImageIndex === null ||
        !propertyBase ||
        !Array.isArray(propertyBase.images) ||
        propertyBase.images.length === 0
      ) {
        return;
      }

      if (event.key === "ArrowLeft") {
        event.preventDefault();
        setSelectedImageIndex(
          selectedImageIndex > 0
            ? selectedImageIndex - 1
            : propertyBase.images.length - 1,
        );
        return;
      }

      if (event.key === "ArrowRight") {
        event.preventDefault();
        setSelectedImageIndex(
          selectedImageIndex < propertyBase.images.length - 1
            ? selectedImageIndex + 1
            : 0,
        );
        return;
      }

      if (event.key === "Escape") {
        event.preventDefault();
        setSelectedImageIndex(null);
      }
    },
    [propertyBase, selectedImageIndex],
  );

  useEffect(() => {
    if (selectedImageIndex === null) {
      return;
    }

    document.addEventListener("keydown", handleGalleryKeyDown);

    return () => {
      document.removeEventListener("keydown", handleGalleryKeyDown);
    };
  }, [handleGalleryKeyDown, selectedImageIndex]);

  const visiblePropertyTags = useMemo(() => {
    if (
      !propertyBase ||
      !Array.isArray(propertyBase.tags) ||
      !Array.isArray(tags)
    ) {
      return [] as PropertyTag[];
    }

    const propertyTags = propertyBase.tags.reduce(
      (result: PropertyTag[], tagId) => {
        const matchingTag = tags.find((tag) => tag._id === tagId);
        if (matchingTag) {
          result.push(matchingTag);
        }
        return result;
      },
      [],
    );

    // a SOLD! or Rented listing shows only that tag
    const statusTags = getStatusTags(propertyTags);
    if (statusTags.length === 0) {
      return propertyTags;
    }

    return statusTags;
  }, [propertyBase, tags]);

  const propertySummaryItems = useMemo(() => {
    const summaryItems: PropertyDetailListItem[] = [];

    if (!propertyBase) {
      return summaryItems;
    }

    summaryItems.push({
      label: "Reference ID",
      value: propertyBase.reff_id || "Not provided",
    });

    summaryItems.push({
      label: "Coordinates",
      value:
        propertyBase.latitude !== undefined &&
        propertyBase.longitude !== undefined
          ? (
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${propertyBase.latitude},${propertyBase.longitude}`}
                target="_blank"
                rel="noreferrer"
                className="underline decoration-muted-foreground underline-offset-4 transition-colors hover:text-primary"
              >
                {propertyBase.latitude}, {propertyBase.longitude}
              </a>
            )
          : "Not provided",
    });

    summaryItems.push({
      label: "Kondisi SHM",
      value: propertyBase.shm_condition || "Not provided",
    });

    summaryItems.push({
      label: "Kondisi SLF/PBG",
      value: propertyBase.slf_pbg_condition || "Not provided",
    });

    summaryItems.push({
      label: "Zonasi",
      value: propertyBase.zoning || "Not provided",
    });

    summaryItems.push({
      label: "Created",
      value: formatDate(propertyBase.created_at),
    });

    summaryItems.push({
      label: "Created by",
      value: propertyBase.created_by
        ? formatCreator(propertyBase.created_by)
        : "Not provided",
    });

    return summaryItems;
  }, [formatCreator, formatDate, propertyBase]);

  if (!propertyBase) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="right"
          className="w-full overflow-y-auto p-0 sm:max-w-2xl lg:max-w-4xl"
        >
          <SheetHeader className="space-y-2 border-b bg-background px-6 py-5 pr-14 text-left">
            <SheetTitle className="text-2xl font-semibold tracking-tight">
              Property Details
            </SheetTitle>
            <SheetDescription>
              Review the full property record. The key facts come first, with
              the rest grouped below.
            </SheetDescription>
          </SheetHeader>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full overflow-y-auto p-0 sm:max-w-2xl lg:max-w-4xl"
      >
        <SheetHeader className="space-y-2 border-b bg-background px-6 py-5 pr-14 text-left">
          <SheetTitle className="text-2xl font-semibold tracking-tight">
            Property Details
          </SheetTitle>
          <SheetDescription>
            Review the full property record. The key facts come first, with the
            rest grouped below.
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-6 px-6 py-5">
          <section className="space-y-5">
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-2">
                {propertyBase.featured && (
                  <Badge variant="default" className="text-xs">
                    <Star className="mr-1 h-3 w-3" />
                    Featured
                  </Badge>
                )}
                {propertyBase.draft ? (
                  <Badge variant="secondary" className="text-xs">
                    Draft
                  </Badge>
                ) : (
                  <Badge variant="default" className="text-xs">
                    Published
                  </Badge>
                )}
                {propertyBase.hidden && (
                  <Badge variant="destructive" className="text-xs">
                    Hidden
                  </Badge>
                )}
              </div>

              <div className="space-y-2">
                <h2 className="text-2xl font-semibold leading-tight text-foreground">
                  {propertyBase.property_name || "Untitled property"}
                </h2>
                <div className="space-y-1 text-sm text-muted-foreground">
                  <p>{propertyBase.address || "Address not provided"}</p>
                  {propertyBase.latitude !== undefined &&
                    propertyBase.longitude !== undefined && (
                      <p>
                        Coordinates: {propertyBase.latitude},{" "}
                        {propertyBase.longitude}
                      </p>
                    )}
                </div>
              </div>

              {propertyTypes.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {propertyTypes.map((type) => (
                    <Badge
                      key={type}
                      variant="outline"
                      className="rounded-full px-3 py-1 text-sm"
                    >
                      {formatPropertyType(type)}
                    </Badge>
                  ))}
                </div>
              )}

              {visiblePropertyTags.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {visiblePropertyTags.map((tag) => (
                    <Badge
                      key={tag._id}
                      variant={
                        isStatusTagName(tag.tag_name) ? "destructive" : "outline"
                      }
                      className="rounded-full px-3 py-1 text-sm"
                    >
                      {tag.tag_name}
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            <div className="rounded-xl border bg-muted/15 p-4">
              <PropertyDetailList items={propertySummaryItems} />
            </div>

            {Array.isArray(propertyBase.images) && propertyBase.images.length > 0 && (
              <section className="space-y-3 rounded-xl border px-4 py-4">
                <div className="space-y-1">
                  <h3 className="text-base font-semibold text-foreground">
                    Images
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {propertyBase.images.length} image
                    {propertyBase.images.length === 1 ? "" : "s"} available.
                    Click to open the gallery.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedImageIndex(0)}
                  className="block w-full rounded-xl text-left outline-none ring-offset-background transition hover:opacity-90 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  <PropertyImageThumbnail
                    images={propertyBase.images}
                    className="w-full"
                    size="lg"
                    showCount={true}
                    maxImages={4}
                  />
                </button>
              </section>
            )}

            {propertyBase.description && (
              <section className="space-y-3 rounded-xl border px-4 py-4">
                <div className="space-y-1">
                  <h3 className="text-base font-semibold text-foreground">
                    Description
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    A quick read of the listing text.
                  </p>
                </div>
                <div
                  className="text-sm leading-6 text-foreground [&_p]:mb-2 [&_p:last-child]:mb-0 [&_strong]:font-semibold [&_em]:italic [&_ul]:mb-3 [&_ul]:list-outside [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:mb-3 [&_ol]:list-outside [&_ol]:list-decimal [&_ol]:pl-6 [&_li]:mb-1"
                  dangerouslySetInnerHTML={{
                    __html: propertyBase.description,
                  }}
                />
              </section>
            )}
          </section>

          <Accordion
            type="multiple"
            defaultValue={["type-details", "facilities"]}
            className="rounded-xl border px-4"
          >
            {(typeDataMap && Object.keys(typeDataMap).length > 0) ||
            singleTypeData ? (
              <AccordionItem value="type-details">
                <AccordionTrigger className="py-5 hover:no-underline">
                  <div className="space-y-1">
                    <p className="text-base font-semibold text-foreground">
                      Property type details
                    </p>
                    <p className="text-sm font-normal text-muted-foreground">
                      Bedrooms, sizes, prices, and type-specific fields.
                    </p>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="space-y-5">
                  {isDetailedQueryLoading && (
                    <p className="text-sm text-muted-foreground">
                      Loading the full property type breakdown.
                    </p>
                  )}

                  {typeDataMap && Object.keys(typeDataMap).length > 0 ? (
                    Object.entries(typeDataMap).map(([type, data]) => {
                      const detailItems: PropertyDetailListItem[] = [];

                      if (
                        type === "rentals" ||
                        type === "freehold_properties" ||
                        type === "freehold_villas" ||
                        type === "leasehold_villas"
                      ) {
                        if (data.bedrooms !== undefined) {
                          detailItems.push({
                            label: "Bedrooms",
                            value: data.bedrooms,
                          });
                        }
                        if (data.bathrooms !== undefined) {
                          detailItems.push({
                            label: "Bathrooms",
                            value: data.bathrooms,
                          });
                        }
                        if (data.build_size !== undefined) {
                          detailItems.push({
                            label: "Build size",
                            value: `${data.build_size} sqm`,
                          });
                        }
                        if (data.land_size !== undefined) {
                          detailItems.push({
                            label: "Land size",
                            value: `${data.land_size} sqm`,
                          });
                        }
                      }

                      if (type === "rentals") {
                        if (data.price_per_year !== undefined) {
                          detailItems.push({
                            label: "Price per year",
                            value: formatPriceInRupiah(data.price_per_year),
                          });
                        }
                        if (data.minimum_duration !== undefined) {
                          detailItems.push({
                            label: "Minimum duration",
                            value: `${data.minimum_duration} months`,
                          });
                        }
                        if (data.safety_deposit_amount !== undefined) {
                          detailItems.push({
                            label: "Security deposit",
                            value: formatPriceInRupiah(
                              data.safety_deposit_amount,
                            ),
                          });
                        }
                        if (data.pet_allowed) {
                          detailItems.push({
                            label: "Pets allowed",
                            value: formatDisplayText(data.pet_allowed),
                          });
                        }
                      }

                      if (
                        type === "freehold_properties" ||
                        type === "freehold_villas"
                      ) {
                        if (data.total_price !== undefined) {
                          detailItems.push({
                            label: "Total price",
                            value: formatPriceInRupiah(data.total_price),
                          });
                        }
                        if (data.dp_minimal !== undefined) {
                          const downPaymentValue =
                            data.total_price !== undefined && data.total_price > 0
                              ? `${formatPercentage(data.dp_minimal)}% (${formatPriceInRupiah(
                                  (data.total_price * data.dp_minimal) / 100,
                                )})`
                              : `${formatPercentage(data.dp_minimal)}%`;
                          detailItems.push({
                            label: "Minimal down payment",
                            value: downPaymentValue,
                          });
                        }
                        if (data.year_built !== undefined) {
                          detailItems.push({
                            label: "Year built",
                            value: data.year_built,
                          });
                        }
                        if (data.pks_bank) {
                          detailItems.push({
                            label: "Bank",
                            value: data.pks_bank,
                          });
                        }
                      }

                      if (type === "leasehold_villas") {
                        if (data.price_for_duration !== undefined) {
                          detailItems.push({
                            label: "Price for duration",
                            value: formatPriceInRupiah(data.price_for_duration),
                          });
                        }
                        if (data.leasehold_duration !== undefined) {
                          detailItems.push({
                            label: "Leasehold duration",
                            value: `${data.leasehold_duration} years`,
                          });
                        }
                        if (data.extension_duration !== undefined) {
                          detailItems.push({
                            label: "Extension duration",
                            value: `${data.extension_duration} years`,
                          });
                        }
                        if (data.year_built !== undefined) {
                          detailItems.push({
                            label: "Year built",
                            value: data.year_built,
                          });
                        }
                      }

                      if (
                        type === "freehold_lands" ||
                        type === "leasehold_lands"
                      ) {
                        if (data.land_size !== undefined) {
                          detailItems.push({
                            label: "Land size",
                            value: `${data.land_size} are`,
                          });
                        }
                        if (data.price_per_are !== undefined) {
                          detailItems.push({
                            label: "Price per are",
                            value: formatPriceInRupiah(data.price_per_are),
                          });
                        }
                        if (data.total_price !== undefined) {
                          detailItems.push({
                            label: "Total price",
                            value: formatPriceInRupiah(data.total_price),
                          });
                        }
                        if (data.land_zoning) {
                          detailItems.push({
                            label: "Land zoning",
                            value: formatDisplayText(data.land_zoning),
                          });
                        }
                        if (data.view) {
                          detailItems.push({
                            label: "View",
                            value: formatDisplayText(data.view),
                          });
                        }
                      }

                      if (type === "freehold_lands" && data.road_access_width) {
                        detailItems.push({
                          label: "Road access width",
                          value: `${data.road_access_width} meters`,
                        });
                      }

                      if (type === "leasehold_lands" && data.lease_duration) {
                        detailItems.push({
                          label: "Lease duration",
                          value: `${data.lease_duration} years`,
                        });
                      }

                      if (detailItems.length === 0) {
                        return null;
                      }

                      return (
                        <section
                          key={type}
                          className="space-y-3 border-t pt-5 first:border-t-0 first:pt-0"
                        >
                          <h3 className="text-sm font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                            {formatPropertyType(type)}
                          </h3>
                          <PropertyDetailList items={detailItems} />
                        </section>
                      );
                    })
                  ) : (
                    <PropertyDetailList
                      items={[
                        ...(singleTypeData &&
                        singleTypeData.bedrooms !== undefined
                          ? [
                              {
                                label: "Bedrooms",
                                value: singleTypeData.bedrooms,
                              },
                            ]
                          : []),
                        ...(singleTypeData &&
                        singleTypeData.bathrooms !== undefined
                          ? [
                              {
                                label: "Bathrooms",
                                value: singleTypeData.bathrooms,
                              },
                            ]
                          : []),
                        ...(singleTypeData &&
                        singleTypeData.build_size !== undefined
                          ? [
                              {
                                label: "Build size",
                                value: `${singleTypeData.build_size} sqm`,
                              },
                            ]
                          : []),
                        ...(singleTypeData &&
                        singleTypeData.land_size !== undefined
                          ? [
                              {
                                label: "Land size",
                                value: `${singleTypeData.land_size} sqm`,
                              },
                            ]
                          : []),
                      ]}
                    />
                  )}
                </AccordionContent>
              </AccordionItem>
            ) : null}

            {property.facilities && (
              <AccordionItem value="facilities">
                <AccordionTrigger className="py-5 hover:no-underline">
                  <div className="space-y-1">
                    <p className="text-base font-semibold text-foreground">
                      Facilities
                    </p>
                    <p className="text-sm font-normal text-muted-foreground">
                      Utility, room, and amenity information.
                    </p>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="space-y-4">
                  <PropertyDetailList
                    items={[
                      ...(property.facilities.living_room_type
                        ? [
                            {
                              label: "Living room type",
                              value: formatDisplayText(
                                property.facilities.living_room_type,
                              ),
                            },
                          ]
                        : []),
                      ...(property.facilities.dining_rooms
                        ? [
                            {
                              label: "Dining rooms",
                              value: property.facilities.dining_rooms,
                            },
                          ]
                        : []),
                      ...(property.facilities.kitchens
                        ? [
                            {
                              label: "Kitchens",
                              value: property.facilities.kitchens,
                            },
                          ]
                        : []),
                      ...(property.facilities.extra_rooms
                        ? [
                            {
                              label: "Extra rooms",
                              value: property.facilities.extra_rooms,
                            },
                          ]
                        : []),
                      ...(property.facilities.electricity_power
                        ? [
                            {
                              label: "Electricity power",
                              value: `${property.facilities.electricity_power} watts`,
                            },
                          ]
                        : []),
                      ...(property.facilities.water_source
                        ? [
                            {
                              label: "Water source",
                              value: formatDisplayText(
                                property.facilities.water_source,
                              ),
                            },
                          ]
                        : []),
                      ...(property.facilities.parking
                        ? [
                            {
                              label: "Parking",
                              value: `${property.facilities.parking} spaces`,
                            },
                          ]
                        : []),
                      ...(property.facilities.view
                        ? [
                            {
                              label: "View",
                              value: formatDisplayText(property.facilities.view),
                            },
                          ]
                        : []),
                      ...(property.facilities.furniture
                        ? [
                            {
                              label: "Furniture",
                              value: formatDisplayText(
                                property.facilities.furniture,
                              ),
                            },
                          ]
                        : []),
                    ]}
                  />

                  {(property.facilities.internet ||
                    property.facilities.swimming_pool ||
                    property.facilities.storage ||
                    property.facilities.garden ||
                    property.facilities.security) && (
                    <div className="space-y-2">
                      <p className="text-xs font-medium uppercase tracking-[0.12em] text-muted-foreground">
                        Features
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {property.facilities.internet && (
                          <Badge variant="outline" className="text-xs">
                            Internet
                          </Badge>
                        )}
                        {property.facilities.swimming_pool && (
                          <Badge variant="outline" className="text-xs">
                            Swimming Pool
                          </Badge>
                        )}
                        {property.facilities.storage && (
                          <Badge variant="outline" className="text-xs">
                            Storage
                          </Badge>
                        )}
                        {property.facilities.garden && (
                          <Badge variant="outline" className="text-xs">
                            Garden
                          </Badge>
                        )}
                        {property.facilities.security && (
                          <Badge variant="outline" className="text-xs">
                            Security
                          </Badge>
                        )}
                      </div>
                    </div>
                  )}
                </AccordionContent>
              </AccordionItem>
            )}

            {property.area && (
              <AccordionItem value="area">
                <AccordionTrigger className="py-5 hover:no-underline">
                  <div className="space-y-1">
                    <p className="text-base font-semibold text-foreground">
                      Area information
                    </p>
                    <p className="text-sm font-normal text-muted-foreground">
                      Geographic context for this listing.
                    </p>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <PropertyDetailList
                    items={[
                      {
                        label: "Area name",
                        value: property.area.area_name,
                      },
                      {
                        label: "Country",
                        value: property.area.country,
                      },
                      {
                        label: "Province",
                        value: property.area.province,
                      },
                    ]}
                  />
                </AccordionContent>
              </AccordionItem>
            )}

            <AccordionItem value="metadata">
              <AccordionTrigger className="py-5 hover:no-underline">
                <div className="space-y-1">
                  <p className="text-base font-semibold text-foreground">
                    Metadata
                  </p>
                  <p className="text-sm font-normal text-muted-foreground">
                    Record timestamps and ownership details.
                  </p>
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <PropertyDetailList
                  items={[
                    {
                      label: "Created",
                      value: formatDate(propertyBase.created_at),
                    },
                    {
                      label: "Created by",
                      value: propertyBase.created_by
                        ? formatCreator(propertyBase.created_by)
                        : "Not provided",
                    },
                  ]}
                />
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </SheetContent>
      <Dialog
        open={selectedImageIndex !== null}
        onOpenChange={() => setSelectedImageIndex(null)}
      >
        <DialogContent className="max-w-5xl max-h-[90vh] p-0">
          <div className="relative bg-black">
            <Button
              variant="ghost"
              size="sm"
              className="absolute right-3 top-3 z-10 bg-black/30 text-white hover:bg-black/50 hover:text-white"
              onClick={() => setSelectedImageIndex(null)}
              aria-label="Close image gallery"
            >
              <X className="h-4 w-4" />
            </Button>

            {selectedImageIndex !== null &&
              Array.isArray(propertyBase.images) &&
              propertyBase.images[selectedImageIndex] && (
                <>
                  <div className="flex min-h-[60vh] items-center justify-center">
                    <ImageDisplay
                      imageKey={propertyBase.images[selectedImageIndex].key}
                      blurhash={propertyBase.images[selectedImageIndex].blurhash}
                      alt={propertyBase.images[selectedImageIndex].filename}
                      className="max-h-[80vh] max-w-full object-contain"
                      objectFit="contain"
                    />
                  </div>

                  {propertyBase.images.length > 1 && (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="absolute left-3 top-1/2 z-10 -translate-y-1/2 bg-black/30 text-white hover:bg-black/50 hover:text-white"
                        onClick={() =>
                          setSelectedImageIndex(
                            selectedImageIndex > 0
                              ? selectedImageIndex - 1
                              : propertyBase.images.length - 1,
                          )
                        }
                        aria-label="Show previous image"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="absolute right-3 top-1/2 z-10 -translate-y-1/2 bg-black/30 text-white hover:bg-black/50 hover:text-white"
                        onClick={() =>
                          setSelectedImageIndex(
                            selectedImageIndex < propertyBase.images.length - 1
                              ? selectedImageIndex + 1
                              : 0,
                          )
                        }
                        aria-label="Show next image"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </>
                  )}

                  <div className="absolute inset-x-0 bottom-0 bg-black/60 px-5 py-4 text-white">
                    <div className="flex items-center justify-between gap-4">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium">
                          {propertyBase.images[selectedImageIndex].filename}
                        </p>
                      </div>
                      <p className="text-sm text-white/75">
                        {selectedImageIndex + 1} of {propertyBase.images.length}
                      </p>
                    </div>
                  </div>
                </>
              )}
          </div>
        </DialogContent>
      </Dialog>
    </Sheet>
  );
}
